import type { Product } from '@/types'

import { useState } from 'react'

import { cn } from '@/lib/utils'
import { useCart } from '@/lib/cart'

const money = (value: number) => `$${value.toFixed(2)}`

// Larger sizes cost a little more.
const SIZE_STEP = 14

export function ProductBuyPanel({ product }: { product: Product }) {
    const { addItem } = useCart()
    const hasSizes = product.sizes.length > 0
    const [sizeIndex, setSizeIndex] = useState(0)
    const [quantity, setQuantity] = useState(1)
    const [added, setAdded] = useState(false)

    const size = hasSizes ? product.sizes[sizeIndex] : ''
    const unitPrice = product.price + (hasSizes ? sizeIndex * SIZE_STEP : 0)

    const handleAdd = () => {
        addItem(
            {
                ...product,
                slug: size ? `${product.slug}--${size.toLowerCase()}` : product.slug,
                name: size ? `${product.name} — ${size}` : product.name,
                price: unitPrice
            },
            quantity
        )
        setAdded(true)
        window.setTimeout(() => setAdded(false), 1800)
    }

    return (
        <div className='product-buy'>
            <div className='product-price-row'>
                <span className='product-price-now'>{money(unitPrice)} USD</span>
                {hasSizes && product.sizes.length > 1 ? (
                    <span className='product-price-note'>
                        from {money(product.price)}
                    </span>
                ) : null}
            </div>

            {hasSizes ? (
                <div className='product-options'>
                    <div className='product-option-label'>Size</div>
                    <div className='product-option-pills'>
                        {product.sizes.map((option, index) => (
                            <button
                                key={option}
                                type='button'
                                className={cn(
                                    'product-pill',
                                    index === sizeIndex && 'is-selected'
                                )}
                                onClick={() => setSizeIndex(index)}
                                style={{ cursor: 'none' }}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className='product-buy-actions'>
                <div className='cart-stepper product-qty'>
                    <button
                        type='button'
                        aria-label='Decrease quantity'
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    >
                        −
                    </button>
                    <span>{quantity}</span>
                    <button
                        type='button'
                        aria-label='Increase quantity'
                        onClick={() => setQuantity((q) => q + 1)}
                    >
                        +
                    </button>
                </div>
                <button
                    type='button'
                    className={cn('button product-add', added && 'is-added')}
                    onClick={handleAdd}
                    style={{ cursor: 'none' }}
                >
                    {added
                        ? 'Added to bag ✓'
                        : `Add to cart — ${money(unitPrice * quantity)}`}
                </button>
            </div>

            <div className='product-assurances'>
                <span>In stock</span>
                <span aria-hidden>·</span>
                <span>
                    {product.category === 'Digital'
                        ? 'Instant download'
                        : 'Ships in 2–3 days'}
                </span>
                <span aria-hidden>·</span>
                <span>Secure checkout</span>
            </div>
        </div>
    )
}
